import { Progress } from "@coval/ui/progress"

import {
  describeDecisionUnavailableReason,
  type DecisionDistributionEntry,
  type LegacyDecisionEstimate,
} from "./legacyDecisionEstimate"

interface LegacyDecisionEstimateDetailProps {
  calibration: LegacyDecisionEstimate
}

export function LegacyDecisionEstimateDetail({
  calibration,
}: LegacyDecisionEstimateDetailProps) {
  if (calibration.status === "unavailable") {
    return (
      <p className="text-sm text-coval-text-muted">
        {describeDecisionUnavailableReason(calibration.reason)}
      </p>
    )
  }

  return (
    <div className="space-y-3 text-sm text-coval-text">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium tabular-nums">
          {describeDecisionValue(calibration)}
        </span>
        {calibration.confidence !== null && (
          <span className="text-coval-text-muted tabular-nums">
            Estimate certainty {formatPercent(calibration.confidence)}
          </span>
        )}
      </div>
      <ul aria-label="Historical answer probabilities" className="space-y-1.5">
        {calibration.distribution.map((entry) => (
          <DistributionBar key={entry.label} entry={entry} />
        ))}
      </ul>
      <p className="text-xs text-coval-text-muted">
        This historical estimate was recorded separately from the judge. It did
        not change the verdict or explanation, and is not the current Coval
        Judge confidence assessment.
      </p>
    </div>
  )
}

function DistributionBar({ entry }: { entry: DecisionDistributionEntry }) {
  const percent = formatPercent(entry.probability)
  return (
    <li className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span
          className="min-w-0 truncate text-coval-text-muted"
          title={entry.label}
        >
          {entry.label}
        </span>
        <span className="shrink-0 tabular-nums">{percent}</span>
      </div>
      <Progress
        aria-label={`Historical estimated probability for ${entry.label}`}
        value={entry.probability * 100}
      />
    </li>
  )
}

function describeDecisionValue(
  calibration: Extract<LegacyDecisionEstimate, { status: "ok" }>,
): string {
  if (calibration.questionType === "choice") {
    return String(calibration.value)
  }
  if (calibration.questionType === "noul") {
    return `${formatPercent(Number(calibration.value))} likely YES`
  }
  const mostLikely = calibration.distribution.reduce((best, entry) =>
    entry.probability > best.probability ? entry : best,
  )
  return `Expected score ${Number(calibration.value).toFixed(2)} · most likely ${mostLikely.label}`
}

function formatPercent(probability: number): string {
  return `${Math.round(probability * 100)}%`
}
